import { useEffect, useMemo, useRef } from 'react';
import { ColumnColoringMode } from '@shared/utils/Table/TableColumn.interface';
export function useTablePrevValuesCache(columns, rows, subscribeRowUpdate) {
    const prevValuesCache = useRef(new Map());
    const prevValuesColumns = useMemo(() => columns.filter((column) => column.coloringMode === ColumnColoringMode.Previous), [columns]);
    const initializeRowPrevValues = (row, prevValuesCacheColumns) => {
        const rowPrevValuesCache = new Map();
        for (const column of prevValuesCacheColumns) {
            const newValue = row.data.getValue(column.id);
            rowPrevValuesCache.set(column.id, { prevValue: undefined, newValue });
        }
        prevValuesCache.current.set(row.id, rowPrevValuesCache);
    };
    const updateRowPrevValues = (row, prevValuesCacheColumns) => {
        var _a;
        const prevValues = prevValuesCache.current;
        const prevValuesRow = prevValues.get(row.id);
        for (const column of prevValuesCacheColumns) {
            const prevValue = (_a = prevValuesRow.get(column.id)) === null || _a === void 0 ? void 0 : _a.newValue;
            const newValue = row.data.getValue(column.id);
            if (prevValue === newValue) {
                continue;
            }
            prevValuesRow.set(column.id, { prevValue: prevValue, newValue });
        }
    };
    // Subscribe to rows changes (Add/Remove)
    useEffect(() => {
        const prevValues = prevValuesCache.current;
        const removedRowIds = new Set(prevValues.keys());
        for (const row of rows) {
            if (!prevValues.has(row.id)) {
                initializeRowPrevValues(row, prevValuesColumns);
            }
            removedRowIds.delete(row.id);
        }
        for (const rowId of removedRowIds) {
            prevValues.delete(rowId);
        }
    }, [rows, prevValuesColumns]);
    // Subscribe to row updates
    useEffect(() => {
        if (isNullOrUndefined(subscribeRowUpdate)) {
            return;
        }
        const unsubscribers = [];
        for (const row of rows) {
            unsubscribers.push(subscribeRowUpdate(row.id, () => updateRowPrevValues(row, prevValuesColumns)));
        }
        return () => {
            for (const unsubscribe of unsubscribers) {
                unsubscribe();
            }
        };
    }, [subscribeRowUpdate, rows, prevValuesColumns]);
    return {
        prevValuesCache: prevValuesCache.current
    };
}
